import { objectWithoutProperties as _objectWithoutProperties, objectSpread as _objectSpread, extends as _extends, inherits as _inherits, classCallCheck as _classCallCheck, possibleConstructorReturn as _possibleConstructorReturn, getPrototypeOf as _getPrototypeOf, createClass as _createClass } from '../_virtual/_rollupPluginBabelHelpers.js';
import React, { forwardRef, Component } from 'react';
import assignWith from 'lodash/assignWith';
import isUndefined from 'lodash/fp/isUndefined';
import hoistStatics from 'hoist-non-react-statics';

var mergeProps = function mergeProps(ownProps, contextProps) {
  return assignWith(_objectSpread({}, ownProps), contextProps, function (ownPropValue, contextValue, key) {
    if (!isUndefined(contextValue) && (key.startsWith('on') || isUndefined(ownPropValue))) {
      return contextValue;
    }

    return ownPropValue;
  });
};
/*
  A component that registers itself with a <Provider> by calling a `register` 
  callback in its constuctor. The data passed to `register` is the result of
  calling the `mapPropsToRegister` function.
*/


var RegisterConsumer = /*#__PURE__*/function (_Component) {
  _inherits(RegisterConsumer, _Component);

  function RegisterConsumer(props) {
    var _this;

    _classCallCheck(this, RegisterConsumer);

    _this = _possibleConstructorReturn(this, _getPrototypeOf(RegisterConsumer).call(this, props));
    var _this$props = _this.props,
        register = _this$props.register,
        mapPropsToRegister = _this$props.mapPropsToRegister,
        mergedProps = _this$props.mergedProps;
    register(mapPropsToRegister(mergedProps));
    return _this;
  }

  _createClass(RegisterConsumer, [{
    key: "render",
    value: function render() {
      var _this$props2 = this.props,
          Component = _this$props2.component,
          mergedProps = _this$props2.mergedProps,
          forwardedRef = _this$props2.forwardedRef;
      return React.createElement(Component, _extends({}, mergedProps, {
        ref: forwardedRef
      }));
    }
  }]);

  return RegisterConsumer;
}(Component);
/*
  A higher-order component to connect dumb components to a context <Consumer>.
  For example the Field atom has a context and passes data to its <Provider>,
  then this is used to hook up Label, Input etc. with the matching <Consumer>.
  See https://reactjs.org/docs/context.html.
  The `mapContextToProps` function may be used to pick fields from the data to
  merge with the wrapped component's props.
  The `mapPropsToRegister` function may be used to pick fields from the merged
  props to send back up to the <Provider>.
*/


var withContext = function withContext(Consumer) {
  var mapContextToProps = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : function (value) {
    return value;
  };
  var mapPropsToRegister = arguments.length > 2 ? arguments[2] : undefined;
  return function (Component) {
    // Forward refs.
    // See https://reactjs.org/docs/forwarding-refs.html#forwarding-refs-in-higher-order-components
    var C = forwardRef(function (props, ref) {
      return React.createElement(Consumer, null, function (value) {
        var register = value.register,
            rest = _objectWithoutProperties(value, ["register"]);

        var mergedProps = mergeProps(props, mapContextToProps(rest, props));

        if (register && mapPropsToRegister) {
          return React.createElement(RegisterConsumer, {
            register: register,
            mapPropsToRegister: mapPropsToRegister,
            component: Component,
            mergedProps: mergedProps,
            forwardedRef: ref
          });
        }

        return React.createElement(Component, _extends({}, mergedProps, {
          ref: ref
        }));
      });
    }); // Define a display name for the React devtools.
    // See https://reactjs.org/docs/higher-order-components.html#convention-wrap-the-display-name-for-easy-debugging.

    C.displayName = Component.displayName || Component.name || 'Component';
    C.WrappedComponent = Component;
    return hoistStatics(C, Component, {
      propTypes: true
    });
  };
};

export default withContext;
export { mergeProps };